import * as THREE from 'three';
import { LocalizationState, type POI, type Route } from './types';
import { loadConfig, saveConfig, isConfigComplete } from './services/configService';
import { MultisetRestClient } from './services/multisetRestClient';
import { NavMeARSession } from './services/multisetClient';
import { EightWallSession } from './services/eightWallSession';
import { PoseTransformService } from './services/poseTransformService';
import { calculateRoute } from './services/routeService';
import { loadNavmesh, getNavmeshDebugData, isNavmeshLoaded } from './services/navmeshService';
import { Map2D } from './components/Map2D';
import { TutorialOverlay, type TutorialStep } from './components/TutorialOverlay';
import { loadOccluder, updateOccluderLOD, setOccluderEnabled, isOccluderEnabled, hasOccluder } from './services/occluderService';
import { MediaLayer } from './components/MediaLayer';
import { autoCaptureAtPoi, submitCaptureNote, scheduleAutoReport, capturePhoto } from './services/captureService';
import { FacilityReportSheet } from './components/FacilityReportSheet';
import { CheckinSheet } from './components/CheckinSheet';
import { submitFacilityRequest } from './services/facilityService';
import { submitCheckin } from './services/checkinService';
import { coachLocalization, resetCoach } from './services/locCoachService';
import { CaptureReviewSheet } from './components/CaptureReviewSheet';
import { logEvent, persistSession, recordLocSample, recordPoseSample, markArSessionStart, markLocalized, recordFps } from './services/debugLog';
import { isVoiceEnabled, setVoiceEnabled, speak, vibrate } from './services/voiceService';
import { getPoiById, getPois } from './services/poiService';
import type { Tour } from './services/tourService';
import { NavigationOverlay } from './components/NavigationOverlay';
import { LocalizationStatusView } from './components/LocalizationStatus';
import { DestinationPicker } from './components/DestinationPicker';
import { DebugPanel } from './components/DebugPanel';
import { SetupScreen } from './components/SetupScreen';
// AdminScreen is dynamically imported (see showAdmin) — it pulls in
// TransformControls/loaders and is visitor-irrelevant; keep it off the
// critical path.
import { StatsHud } from './components/StatsHud';
import { MarkPoiForm } from './components/MarkPoiForm';
import { createPoiWithNode } from './services/poiCreationService';
import { submitPoiSuggestion } from './services/poiSuggestionService';
import { dist3, formatMetres, throttle, FpsMeter, type TrailGuide } from './utils/math';
import { diagDisabled, diagDisabledList } from './utils/diagnostics';
import { t, getLang, setLang, LANGS } from './i18n';
import { getEnabledLangs, loadFeatures, featureEnabled } from './services/venueSettingsService';
import { getFloorPlans, getFloors } from './services/floorPlanService';
import { ExploreLayer } from './components/ExploreLayer';
import { track } from './services/analyticsService';
import { saveParkingSpot, getParkingSpot, clearParkingSpot } from './services/parkingService';
import { isEmbedded, wantsPositionStream, applyEmbedTheme, emitEmbed, initEmbedCommands } from './services/embedService';
import { ConciergeSheet } from './components/ConciergeSheet';
import { TranslateOverlay } from './components/TranslateOverlay';
import { isConciergeAvailable } from './services/conciergeService';
import { getTours } from './services/tourService';
import { escapeHtml, isSafeHttpUrl } from './utils/html';
import { consumeOAuthRedirect } from './services/supabaseClient';

/**
 * NavMe by MetaDigi Labs — app orchestration.
 * Flow: landing → (setup) → pre-authorize → Start AR → localize → pick
 * destination → AR guidance → arrival.
 *
 * Two mutually-exclusive modes, chosen once at boot in init():
 *  - 'webxr':  Android Chrome / any WebXR-capable browser. NavMeARSession
 *              (multisetClient.ts) drives our own top-level THREE scene.
 *  - 'camera': iOS Safari fallback (no WebXR at all — see README).
 *              EightWallSession (eightWallSession.ts) uses 8th Wall's free
 *              SLAM engine for continuous 6-DOF tracking in stock Safari,
 *              plus MultiSet's REST API for periodic absolute-position fixes,
 *              and drives ITS OWN three.js scene (created by the 8th Wall
 *              engine itself, handed to us via onSceneReady).
 *
 * Both modes ultimately produce a `worldFromMap` matrix consumed by
 * PoseTransformService the exact same way, so NavigationOverlay (the 3D
 * arrow/breadcrumbs/pin) and all guidance logic below is 100% shared
 * between modes via the `NavContext` indirection — no separate "compass
 * mode" UI needed. (A lighter, no-third-party-binary fallback without 8th
 * Wall still exists at cameraArSession.ts + CompassOverlay.ts — not wired
 * in by default; see README "iOS Safari".)
 */

type AppMode = 'webxr' | 'camera';
let mode: AppMode | null = null;

interface NavContext {
  poseTransform: PoseTransformService;
  navOverlay: NavigationOverlay;
  camera: THREE.Camera;
}

const app = document.getElementById('app')!;
const overlayRoot = document.getElementById('overlay-root')!;
if (diagDisabledList().length) logEvent('session', `diagnostics: disabled ${diagDisabledList().join(',')} (?no=)`);
const canvas = document.getElementById('xr-canvas') as HTMLCanvasElement;
const eightWallCanvas = document.getElementById('eightwall-canvas') as HTMLCanvasElement;

// Tap-to-open for AR link buttons (camera mode: 8th Wall owns this canvas).
eightWallCanvas.addEventListener('click', (ev) => {
  const nav = activeNav();
  if (!nav) return;
  const ndcX = (ev.clientX / window.innerWidth) * 2 - 1;
  const ndcY = -(ev.clientY / window.innerHeight) * 2 + 1;
  // Explore labels first (they sit above media), then media interactions.
  if (exploreLayers.get(nav.poseTransform)?.handleTap(nav.camera, ndcX, ndcY)) return;
  mediaLayers.get(nav.poseTransform)?.handleTap(nav.camera, ndcX, ndcY);
});

// ---------- Platform boot: embed theming, ?lang=, host-app commands ----------
applyEmbedTheme();
{
  const bootLang = new URLSearchParams(location.search).get('lang');
  if (bootLang && LANGS.some((l) => l.code === bootLang)) setLang(bootLang as never);
}
initEmbedCommands({
  navigate: (poiId) => {
    void getPoiById(poiId).then((poi) => {
      if (poi) {
        activeTour = null;
        void onDestinationChosen(poi);
      }
    });
  },
  setLang: (lang) => {
    if (LANGS.some((l) => l.code === lang)) {
      setLang(lang as never);
      if (lastLandingOpts) showLanding(lastLandingOpts);
    }
  },
});

// ---------- Three.js scene (webxr mode only, created once, reused across sessions) ----------
const renderer = new THREE.WebGLRenderer({ canvas, antialias: true, alpha: true });
renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
renderer.setSize(window.innerWidth, window.innerHeight);
const scene = new THREE.Scene();
const camera = new THREE.PerspectiveCamera(
  70,
  window.innerWidth / window.innerHeight,
  0.01,
  1000,
);
scene.add(camera);
scene.add(new THREE.AmbientLight(0xffffff, 1));

const webxrNav: NavContext = {
  poseTransform: new PoseTransformService(),
  navOverlay: null as unknown as NavigationOverlay, // set immediately below
  camera,
};
webxrNav.navOverlay = new NavigationOverlay(webxrNav.poseTransform, scene, camera);

/** camera mode only — built lazily once EightWallSession's scene is ready (its own scene/camera, not the one above). */
let cameraModeNav: NavContext | null = null;

function activeNav(): NavContext | null {
  if (mode === 'webxr') return webxrNav;
  if (mode === 'camera') return cameraModeNav;
  return null;
}

// ---------- UI elements ----------
const statusView = new LocalizationStatusView();
const statsHud = new StatsHud();
// VPS/FPS toggle button (📶) lives in the debug panel but controls two
// separate elements — the always-on-screen StatsHud, and (2026-07-18 user
// request) whether the green "Localized" status pill stays visible after
// localization succeeds — see statsHudService.ts / LocalizationStatus.ts.
const debugPanel = new DebugPanel((visible) => {
  statsHud.setVisible(visible);
  statusView.applyVisibility();
});
const fpsMeter = new FpsMeter();
let arSession: NavMeARSession | null = null;
let eightWallSession: EightWallSession | null = null;
let currentRoute: Route | null = null;
let hasShownPicker = false;
/**
 * True once VPS has been failing long enough to enter 'Relocalizing…'
 * (3+ consecutive misses — see eightWallSession/multisetClient). The
 * on-screen path/arrow keep rendering through this (routine outages
 * shouldn't blink the UI), but new floor-change ("take the stairs") cues
 * are suppressed while stale: a debug report (2026-07-11) showed VPS going
 * ~3.5 min without a confirmed fix (likely the app was backgrounded); the
 * camera pose drifts uncorrected the whole time, and by the time VPS came
 * back the drifted height alone was enough to falsely arm "stairs down" on
 * a same-floor route (nodeY=-0.00, but userFloorY had drifted to 1.2m —
 * see `held Δ10.63m 148.3° — awaiting confirmation` right after in the same
 * report). Cleared the moment a fresh localization lands.
 */
let localizationStale = false;
/** Landing screen's status line — updated from onState during authorize() so a slow/hung step (e.g. "Loading AR engine…") is visible instead of a frozen "Connecting to MultiSet…". */
let landingNote: HTMLElement | null = null;
function setLandingNote(text: string): void {
  if (landingNote) landingNote.textContent = text;
}

// ---------- Startup watchdog (Test 21: "stuck" on start) ----------
// After the user taps Start AR, iOS asks for Motion + Camera permission and 8th
// Wall warms the camera + first VPS fix. If a permission prompt is missed, or
// the tab is backgrounded (which pauses the load AND its timers), the app can
// sit on "Localizing…" indefinitely with no way out. This surfaces a clear
// Retry instead of a frozen spinner. (setTimeout is itself paused while
// backgrounded, so we ALSO re-check on visibilitychange when the user returns.)
const STARTUP_WATCHDOG_MS = 15_000;
let startupWatchdog: number | null = null;
let startupPending = false; // Start AR tapped, not yet Localized
function armStartupWatchdog(): void {
  startupPending = true;
  if (startupWatchdog !== null) clearTimeout(startupWatchdog);
  startupWatchdog = window.setTimeout(showStartupStall, STARTUP_WATCHDOG_MS);
}
function watchdogOnState(state: LocalizationState): void {
  if (state === LocalizationState.Localized) {
    startupPending = false;
    if (startupWatchdog !== null) { clearTimeout(startupWatchdog); startupWatchdog = null; }
    document.getElementById('startup-stall')?.remove();
  }
}
function showStartupStall(): void {
  if (!startupPending || document.getElementById('startup-stall')) return;
  logEvent('session', 'startup watchdog: not localized within 15s — showing Retry');
  const el = document.createElement('div');
  el.id = 'startup-stall';
  el.style.cssText =
    'position:fixed;inset:0;z-index:9999;display:flex;flex-direction:column;align-items:center;justify-content:center;gap:18px;background:rgba(2,6,23,0.94);color:#e2e8f0;text-align:center;padding:24px;font:16px system-ui,-apple-system,sans-serif;';
  el.innerHTML =
    '<div style="max-width:320px;line-height:1.5">Still starting…<br><br>Make sure you tapped <b>Allow</b> for camera &amp; motion, and keep the app in the foreground.</div>';
  const btn = document.createElement('button');
  btn.textContent = 'Retry';
  btn.className = 'btn primary';
  btn.addEventListener('click', () => window.location.reload());
  el.appendChild(btn);
  document.body.appendChild(el);
}
// Backgrounding pauses the load + the watchdog timer; when the user returns and
// we're still not localized, prompt immediately rather than waiting out the
// (also-paused) timer.
document.addEventListener('visibilitychange', () => {
  if (document.visibilityState === 'visible' && startupPending) showStartupStall();
});

const picker = new DestinationPicker(
  (poi) => {
    activeTour = null; // manual pick cancels any running tour
    void onDestinationChosen(poi);
  },
  (tour) => startTour(tour),
);

// Tapping the AR view (anywhere outside the sheet / HUD buttons) minimizes
// the destination picker so the camera view is unobstructed. Reopen via 🧭.
// Capture phase so it works regardless of which canvas owns the tap.
document.addEventListener(
  'pointerdown',
  (ev) => {
    if (picker.el.classList.contains('hidden')) return;
    const t = ev.target as Node;
    if (picker.el.contains(t) || controls.contains(t)) return;
    picker.hide();
  },
  true,
);

// Personal parking spot: pinned card in the picker; tap → navigate back.
picker.setPersonalSpot(getParkingSpot());
picker.onSpotSelected = () => {
  const spot = getParkingSpot();
  if (!spot) return;
  activeTour = null;
  void onDestinationChosen({
    id: 'personal-parking',
    name: t('park.mySpot'),
    category: '🚗',
    floor: 0,
    position: spot.position,
    nodeId: '__parking__', // not in the graph — navmesh/direct routing handles it
  });
};
picker.onSpotCleared = () => {
  clearParkingSpot();
  picker.setPersonalSpot(null);
};

// ---------- AI concierge ----------
const concierge = new ConciergeSheet(
  () => {
    const nav = activeNav();
    return nav && nav.poseTransform.isLocalized
      ? nav.poseTransform.snapshot(nav.camera).mapPosition
      : null;
  },
  (poiId) => {
    void getPoiById(poiId).then((poi) => {
      if (poi) {
        activeTour = null;
        void onDestinationChosen(poi);
      }
    });
  },
  (tourId) => {
    void getTours().then((tours) => {
      const tour = tours.find((tt) => tt.id === tourId);
      if (tour) startTour(tour);
    });
  },
  // Multi-stop route planned by the concierge agent ("coffee then restroom")
  // — an ad-hoc tour reuses the whole tour pipeline: per-stop voice, arrival
  // auto-advance, skip-missing-stops.
  (poiIds) => {
    startTour({ id: 'concierge-route', name: t('concierge.routeName'), poiIds });
  },
  // v8: agent extracted a bookmark request ("remember this as...") — arm the
  // SAME tap-to-place mechanism the 🎯 staff button uses, but route the tap
  // to a visitor-writable poi_suggestions insert instead of the staff form.
  (s) => startPlacingSuggestion(s),
);

// ---------- Multi-stop tours ----------
let activeTour: { tour: Tour; idx: number } | null = null;

function startTour(tour: Tour): void {
  if (tour.poiIds.length === 0) return;
  activeTour = { tour, idx: 0 };
  logEvent('route', `tour started "${tour.name}" (${tour.poiIds.length} stops)`);
  track('tour_start');
  speak(t('tour.start', { name: tour.name, n: tour.poiIds.length }), 'tour-start', 2000);
  void goToTourStop();
}

async function goToTourStop(): Promise<void> {
  if (!activeTour) return;
  const { tour, idx } = activeTour;
  const poi = await getPoiById(tour.poiIds[idx]);
  if (!poi) {
    // Stop's POI was deleted — skip it rather than stranding the tour.
    logEvent('error', `tour stop ${idx + 1} missing (${tour.poiIds[idx]}) — skipping`);
    if (idx < tour.poiIds.length - 1) {
      activeTour = { tour, idx: idx + 1 };
      return goToTourStop();
    }
    activeTour = null;
    return;
  }
  speak(t('tour.stop', { i: idx + 1, n: tour.poiIds.length, name: poi.name }), 'tour-stop', 2000);
  await onDestinationChosen(poi);
}

const markPoiForm = new MarkPoiForm(async (result, position) => {
  // createPoiWithNode (poiCreationService.ts) also creates the nav_node +
  // connecting edge and invalidates both the POI and graph caches, so the
  // new POI is immediately searchable/routable without a page reload.
  await createPoiWithNode({
    name: result.name,
    category: result.category,
    floor: result.floor,
    position,
    description: result.description,
  });
});

// HUD (visible only during AR, lives in the DOM overlay)
const hud = document.createElement('div');
hud.className = 'hud hidden';

const guidance = document.createElement('div');
guidance.className = 'guidance hidden';

const arrivalBanner = document.createElement('div');
arrivalBanner.className = 'arrival hidden';
const arrivalTitle = document.createElement('h2');
arrivalBanner.appendChild(arrivalTitle);
const arrivalAgainBtn = document.createElement('button');
arrivalAgainBtn.className = 'btn primary';

/** Arrival banner text resolves at show time (language may have changed). */
function showArrivalBanner(): void {
  arrivalTitle.textContent = t('arrival.title');
  arrivalAgainBtn.textContent = t('arrival.again');
  exhibitNextBtn.classList.add('hidden'); // only exhibit-mode arrivals show it
  arrivalBanner.classList.remove('hidden');
}

// 🖼 Museums pack: manual "Next exhibit ▶" pacing (feature: 'exhibitMode').
const exhibitNextBtn = document.createElement('button');
exhibitNextBtn.className = 'btn primary hidden';
exhibitNextBtn.addEventListener('click', () => {
  if (!activeTour || activeTour.idx >= activeTour.tour.poiIds.length - 1) return;
  exhibitNextBtn.classList.add('hidden');
  arrivalBanner.classList.add('hidden');
  activeTour = { tour: activeTour.tour, idx: activeTour.idx + 1 };
  logEvent('route', `exhibit → next (${activeTour.idx + 1}/${activeTour.tour.poiIds.length})`);
  void goToTourStop();
});
arrivalAgainBtn.addEventListener('click', () => {
  arrivalBanner.classList.add('hidden');
  clearActiveRoute();
  showPicker();
});

/** Opens the picker with the current position (enables distance sorting). */
function showPicker(): void {
  // First-run tutorial owns the whole screen — the POI list would render
  // under/through it (user report 2026-07-12). Defer until it closes.
  if (tutorial.active) {
    afterTutorial = () => showPicker();
    return;
  }
  const nav = activeNav();
  const pos =
    nav && nav.poseTransform.isLocalized
      ? nav.poseTransform.snapshot(nav.camera).mapPosition
      : undefined;
  picker.show(pos);
}
arrivalBanner.appendChild(exhibitNextBtn);
arrivalBanner.appendChild(arrivalAgainBtn);

// Full-screen invisible tap-catcher for "🎯 Tap to place POI" — only
// pointer-events-enabled while placingPoi is true, so it never steals taps
// from the rest of the HUD otherwise.
const tapCatcher = document.createElement('div');
tapCatcher.className = 'tap-catcher hidden';
// v8: when set, the next tapCatcher tap submits a poi_suggestions row
// (visitor-writable, moderation-queued) instead of opening the staff
// markPoiForm — see startPlacingSuggestion() and the tapCatcher listener.
let pendingSuggestion: { name: string; category?: string; description?: string } | null = null;
let placingPoi = false;

const controls = document.createElement('div');
// ctrl-hidden: starts hidden — 2026-07-18 user request ("options come up
// only on screen touch") — see showControls()/scheduleControlsHide() below.
// Only .ctrl-secondary children fade with this state (see styles.css);
// 🧭/✕ (changeBtn/stopBtn, tagged below) stay always visible per a
// same-day follow-up request.
controls.className = 'controls ctrl-hidden';
const relocBtn = iconButton('🔄', t('ctrl.relocalize'), () => void relocalize());
const changeBtn = iconButton('🧭', t('ctrl.change'), () => showPicker());
const voiceBtn = iconButton('🗣️', t('ctrl.voice'), () => {
  setVoiceEnabled(!isVoiceEnabled());
  voiceBtn.classList.toggle('on', isVoiceEnabled());
});
voiceBtn.classList.toggle('on', isVoiceEnabled());
const exploreBtn = iconButton('🏷️', t('ctrl.explore'), () => {
  const nav = activeNav();
  if (!nav) return;
  exploreOn = !exploreOn;
  exploreBtn.classList.toggle('on', exploreOn);
  const layer = exploreLayerFor(nav);
  layer.setVisible(exploreOn);
  if (exploreOn) {
    void layer.load();
    track('explore_on');
  }
});
const parkBtn = iconButton('🚗', t('ctrl.park'), () => {
  const nav = activeNav();
  if (!nav || !nav.poseTransform.isLocalized) return;
  const pos = nav.poseTransform.snapshot(nav.camera).mapPosition;
  saveParkingSpot(pos);
  picker.setPersonalSpot(getParkingSpot());
  track('parking_saved');
  speak(t('park.saved'), 'park', 3000);
  proxToast.textContent = `🚗 ${t('park.saved')}`;
  proxToast.classList.remove('hidden');
  window.clearTimeout(proxToastTimer);
  proxToastTimer = window.setTimeout(() => proxToast.classList.add('hidden'), 3500);
});
function showToastMsg(msg: string): void {
  proxToast.textContent = msg;
  proxToast.classList.remove('hidden');
  window.clearTimeout(proxToastTimer);
  proxToastTimer = window.setTimeout(() => proxToast.classList.add('hidden'), 3500);
}

const conciergeBtn = iconButton('💬', t('ctrl.concierge'), () => {
  if (!isConciergeAvailable()) return;
  picker.hide();
  if (concierge.visible) concierge.hide();
  else concierge.show();
});
const markBtn = iconButton('📍', 'Mark POI here', () => onMarkPoiClicked());
const placeBtn = iconButton('🎯', 'Tap to place POI', () => togglePlacingPoi());
const stopBtn = iconButton('✕', 'Stop navigation', () => stopEverything());
const map2dBtn = iconButton('🗺', t('ctrl.map2d'), () => openMap2D());
// 🏢 Corporate pack: report a facility issue where you stand.
const facBtn = iconButton('🛠', t('fac.title'), () => onFacilityClicked());
// 🔤 Sign translation (OCR_TRANSLATE_SPEC.md): snapshot flow — the overlay
// grabs one frame from the AR canvas; gates (flag/lang/daily cap) are
// enforced server-side in the ocr-translate Edge Fn.
// MODE-AWARE CANVAS (2026-07-18, field debug report): #xr-canvas is
// WebXR-only — camera mode (iOS/8th Wall) renders everything onto
// #eightwall-canvas instead (see index.html). Always reading #xr-canvas
// meant every capture on an iPhone read an empty, never-drawn-to buffer —
// 100% blank regardless of the retry logic in grabFrameJpegRetrying, which
// was retrying against the right timing but the wrong element entirely.
const translateOverlay = new TranslateOverlay(() => (mode === 'webxr' ? canvas : eightWallCanvas));
const translateBtn = iconButton('🔤', t('tr.button'), () => translateOverlay.open());
// 2026-07-18 user follow-up ("show all options instead of 3 dots. Keep
// Navigation bottom button"): the ⋯ "more tools" tray from the
// 2026-07-12 HUD de-clutter build is gone — every one of these now shows
// (or fades away) TOGETHER as a group, controlled directly by
// .controls.ctrl-hidden (see styles.css) instead of a separate ctrl-open
// toggle. changeBtn (🧭) and stopBtn (✕) are deliberately NOT tagged here
// — they stay permanently visible regardless of ctrl-hidden.
for (const b of [relocBtn, voiceBtn, map2dBtn, exploreBtn, parkBtn, conciergeBtn, markBtn, placeBtn, facBtn, translateBtn, debugPanel.toggleButton]) {
  b.classList.add('ctrl-secondary');
}
controls.append(changeBtn, stopBtn, relocBtn, voiceBtn, map2dBtn, exploreBtn, parkBtn, conciergeBtn, markBtn, placeBtn, facBtn, translateBtn, debugPanel.toggleButton);

// 2026-07-18 user request ("options come up only on screen touch"): every
// .ctrl-secondary tool starts hidden (ctrl-hidden at controls' creation
// above) and appears on a tap, auto-hiding again after a few seconds idle
// — same "tap to reveal, fades on its own" pattern as a video player's
// controls. 🧭/✕ are untouched by this (see styles.css — the fade rule
// targets .ctrl-secondary specifically, not the whole .controls column).
let controlsHideTimer: number | null = null;
const CONTROLS_HIDE_DELAY_MS = 3000;

function showControls(): void {
  controls.classList.remove('ctrl-hidden');
  scheduleControlsHide();
}

function scheduleControlsHide(): void {
  if (controlsHideTimer !== null) window.clearTimeout(controlsHideTimer);
  controlsHideTimer = window.setTimeout(() => {
    controls.classList.add('ctrl-hidden');
  }, CONTROLS_HIDE_DELAY_MS);
}

// Any tap on the AR view reveals the controls (and keeps extending the
// idle timer for taps ON the controls themselves — pressing a button
// shouldn't have it vanish mid-interaction). Capture phase + no target
// filtering: this only ever shows something and (re)starts a timer, so
// running it unconditionally on every pointerdown is harmless.
document.addEventListener('pointerdown', () => showControls(), true);

// ---------- 2D map view (AR fallback + in-AR overview) ----------
// Full-screen top-down floor plan: live user dot + route while AR runs,
// plain venue map when AR can't (desktop, camera denied, AR failure).
const map2dOverlay = document.createElement('div');
map2dOverlay.className = 'map2d-overlay hidden';
const map2dTop = document.createElement('div');
map2dTop.className = 'map2d-top';
const map2dTitle = document.createElement('h2');
const map2dClose = document.createElement('button');
map2dClose.className = 'icon-btn';
map2dClose.textContent = '✕';
map2dTop.append(map2dTitle, map2dClose);
const map2dView = new Map2D(
  (poi) => {
    // Sheet's 🧭 Navigate: route when AR is localized, otherwise say where.
    const nav = activeNav();
    if (nav && nav.poseTransform.isLocalized) {
      closeMap2D();
      activeTour = null;
      void onDestinationChosen(poi);
    } else {
      showToastMsg(`${poi.name} — ${poi.category}`);
    }
  },
  undefined,
  { sheet: true }, // visitor mode: tap pin → bottom card w/ distance + Navigate
);
map2dOverlay.append(map2dTop, map2dView.el);
document.body.appendChild(map2dOverlay);
let map2dTimer: number | null = null;

/** Destination we FAILED to route to (no walkable path) — the 2D map then
 *  draws a dashed direction hint instead of nothing (user 2026-07-12:
 *  "2D doesn't show path … doesn't help"). */
let noRouteDest: POI | null = null;

function refreshMap2D(): void {
  const nav = activeNav();
  const snap = nav && nav.poseTransform.isLocalized ? nav.poseTransform.snapshot(nav.camera) : null;
  map2dView.setLive(
    snap ? { x: snap.mapPosition.x, y: snap.mapPosition.y, z: snap.mapPosition.z, headingRad: snap.headingRad } : null,
    currentRoute
      ? [...currentRoute.nodes.map((n) => n.position), currentRoute.destination.position]
      : null,
    !currentRoute && noRouteDest && snap ? [snap.mapPosition, noRouteDest.position] : null,
  );
}

function openMap2D(): void {
  map2dTitle.textContent = t('map2d.title');
  map2dOverlay.classList.remove('hidden');
  void getPois().then((pois) => {
    const feed = () => map2dView.setData(getNavmeshDebugData(), pois);
    if (isNavmeshLoaded()) feed();
    else void loadNavmesh(loadConfig()).then(feed).catch(() => map2dView.setData(null, pois));
  });
  // Architectural floor-plan underlays + explicit floors (Dashboard-config).
  void getFloorPlans(loadConfig().code)
    .then((p) => map2dView.setFloorPlans(p))
    .catch(() => undefined);
  void getFloors(loadConfig().code)
    .then((f) => map2dView.setExplicitFloors(f))
    .catch(() => undefined);
  refreshMap2D();
  map2dView.setActive(true); // animation loop (route dashes, pulse) + hint
  map2dTimer = window.setInterval(refreshMap2D, 800);
  logEvent('session', '2D map opened');
}

function closeMap2D(): void {
  map2dOverlay.classList.add('hidden');
  map2dView.setActive(false);
  if (map2dTimer !== null) {
    window.clearInterval(map2dTimer);
    map2dTimer = null;
  }
}
map2dClose.addEventListener('click', () => closeMap2D());

// ---------- AR-failure fallback (user 2026-07-12: "remember the intent of
// using this experience — if you failed AR, immediately show in 2D map how
// to go; you know the start point and route"). When localization goes blind
// MID-ROUTE and stays blind for a grace period, auto-open the 2D map: the
// last-known position, the route polyline and the destination pin are all
// still valid, so the user keeps moving instead of waving the camera around.
const AR_FALLBACK_GRACE_MS = 8000;
let arFallbackTimer: number | null = null;
let arFallbackBlind = false;

// Fallback is a PROMPT, not an auto-switch (user 2026-07-12: "fallback
// should be prompt … it got localized and instead of AR it went to 2D").
// A card offers the map; the user stays in AR unless they take it.
const fallbackPrompt = document.createElement('div');
fallbackPrompt.className = 'fallback-prompt hidden';
const fpText = document.createElement('p');
const fpBtns = document.createElement('div');
fpBtns.className = 'fallback-prompt-btns';
const fpShow = document.createElement('button');
fpShow.className = 'btn primary';
const fpStay = document.createElement('button');
fpStay.className = 'btn ghost';
fpBtns.append(fpShow, fpStay);
fallbackPrompt.append(fpText, fpBtns);
hud.appendChild(fallbackPrompt);
let fpTimer: number | null = null;
function hideFallbackPrompt(): void {
  fallbackPrompt.classList.add('hidden');
  if (fpTimer !== null) {
    window.clearTimeout(fpTimer);
    fpTimer = null;
  }
}
function showFallbackPrompt(message: string): void {
  fpText.textContent = message;
  fpShow.textContent = t('map2d.show');
  fpStay.textContent = t('map2d.stayAr');
  fallbackPrompt.classList.remove('hidden');
  if (fpTimer !== null) window.clearTimeout(fpTimer);
  fpTimer = window.setTimeout(hideFallbackPrompt, 20_000); // don't nag forever
}
fpShow.addEventListener('click', () => {
  hideFallbackPrompt();
  openMap2D();
});
fpStay.addEventListener('click', hideFallbackPrompt);
function arFallbackOnState(state: LocalizationState): void {
  arFallbackBlind =
    state === LocalizationState.Relocalizing ||
    state === LocalizationState.LostTracking ||
    state === LocalizationState.Error;
  if (!arFallbackBlind) {
    if (arFallbackTimer !== null) {
      window.clearTimeout(arFallbackTimer);
      arFallbackTimer = null;
    }
    return;
  }
  if (arFallbackTimer !== null || !currentRoute || !map2dOverlay.classList.contains('hidden')) return;
  arFallbackTimer = window.setTimeout(() => {
    arFallbackTimer = null;
    if (!arFallbackBlind || !currentRoute) return; // recovered / route ended
    if (!map2dOverlay.classList.contains('hidden')) return; // already open
    if (!featureEnabled('map2d')) return;
    logEvent('session', `AR blind ${AR_FALLBACK_GRACE_MS / 1000}s with active route — offering 2D map fallback`);
    speak(t('map2d.fallback'), 'map-fallback', 30_000);
    showFallbackPrompt(t('map2d.fallback'));
  }, AR_FALLBACK_GRACE_MS);
}

// Layout fix (user screenshot): the button column overlaid the picker's
// distance chips. While the destination sheet is open, slide the controls
// away; they return when the sheet hides.
new MutationObserver(() => {
  controls.classList.toggle('controls-tucked', !picker.el.classList.contains('hidden'));
}).observe(picker.el, { attributes: true, attributeFilter: ['class'] });

// Post-capture review sheet (photo/video preview + rating + notes).
const captureReview = new CaptureReviewSheet();

// 🏢 Corporate pack sheets: facility reporting (in-AR) + visitor check-in
// (landing screen; appended to body so it works before AR starts).
const facilitySheet = new FacilityReportSheet();
const checkinSheet = new CheckinSheet();
document.body.appendChild(checkinSheet.el);

/** 🛠 tapped: pin the report to the user's position + nearest POI. */
function onFacilityClicked(): void {
  picker.hide();
  const nav = activeNav();
  const pos = nav && nav.poseTransform.isLocalized ? nav.poseTransform.snapshot(nav.camera).mapPosition : undefined;
  void getPois()
    .then((pois) => {
      let near: POI | null = null;
      if (pos) {
        let best = Infinity;
        for (const p of pois) {
          const d = Math.hypot(p.position.x - pos.x, p.position.z - pos.z);
          if (d < best) {
            best = d;
            near = p;
          }
        }
        if (best > 15) near = null; // too far to be a useful location hint
      }
      facilitySheet.show(near?.name ?? null, (draft) => {
        if (!draft) return;
        void (async () => {
          let photoUrl: string | undefined;
          if (draft.withPhoto) {
            try {
              photoUrl = (await capturePhoto(canvas)).url;
            } catch {
              /* no camera canvas (2D fallback) — report goes without photo */
            }
          }
          await submitFacilityRequest({
            category: draft.category,
            note: draft.note || undefined,
            reporter: draft.reporter || undefined,
            photoUrl,
            poiId: near?.id,
            poiName: near?.name,
            pos,
            floor: near?.floor,
          });
          showToastMsg(t('fac.sent'));
        })().catch((err: unknown) =>
          showToastMsg(err instanceof Error ? err.message : String(err)),
        );
      });
    })
    .catch(() => facilitySheet.show(null, () => undefined));
}

hud.append(statusView.el, statsHud.el, guidance, controls, arrivalBanner, debugPanel.el, picker.el, concierge.el, markPoiForm.el, captureReview.el, facilitySheet.el, tapCatcher);

// ---- In-AR webpage overlay (link POIs) ----
// The page opens INSIDE the AR view (camera session keeps running behind it)
// instead of redirecting to a new tab. YouTube/Vimeo URLs are converted to
// their embeddable players so videos play in place.
const linkOverlay = document.createElement('div');
linkOverlay.className = 'link-overlay hidden';
const linkHeader = document.createElement('div');
linkHeader.className = 'link-overlay-header';
const linkTitle = document.createElement('span');
linkTitle.className = 'link-overlay-title';
const linkBrowserBtn = document.createElement('a');
linkBrowserBtn.className = 'icon-btn link-overlay-ext';
linkBrowserBtn.textContent = '↗';
linkBrowserBtn.title = 'Open in browser (if the page refuses to embed)';
linkBrowserBtn.target = '_blank';
linkBrowserBtn.rel = 'noopener';
const linkCloseBtn = document.createElement('button');
linkCloseBtn.className = 'icon-btn';
linkCloseBtn.textContent = '✕';
linkHeader.append(linkTitle, linkBrowserBtn, linkCloseBtn);
const linkFrame = document.createElement('iframe');
linkFrame.className = 'link-overlay-frame';
linkFrame.setAttribute('allow', 'autoplay; encrypted-media; picture-in-picture; fullscreen');
// Defense in depth on top of the isSafeHttpUrl() check in openLinkOverlay:
// no allow-same-origin, so even a malicious embedded page can't reach this
// app's origin (localStorage credentials). Embedded players (YouTube/Vimeo)
// work fine under these flags.
linkFrame.setAttribute('sandbox', 'allow-scripts allow-popups allow-presentation');
linkOverlay.append(linkHeader, linkFrame);
hud.appendChild(linkOverlay);

function openLinkOverlay(url: string, name: string): void {
  // Stored mediaUrl is untrusted (writes are network-reachable) — only
  // http(s) may reach an iframe src / anchor href. See utils/html.ts.
  if (!isSafeHttpUrl(url)) {
    logEvent('error', `blocked unsafe link URL for "${name}"`);
    return;
  }
  linkTitle.textContent = name;
  linkBrowserBtn.href = url;
  linkFrame.src = toEmbeddableUrl(url);
  linkOverlay.classList.remove('hidden');
}
function closeLinkOverlay(): void {
  linkFrame.src = 'about:blank'; // stops any playing media
  linkOverlay.classList.add('hidden');
}
linkCloseBtn.addEventListener('click', closeLinkOverlay);

/** YouTube/Vimeo page URLs → embeddable player URLs (page URLs block iframing). */
function toEmbeddableUrl(url: string): string {
  try {
    const u = new URL(url);
    const host = u.hostname.replace(/^www\./, '');
    if (host === 'youtube.com' && u.pathname === '/watch' && u.searchParams.get('v')) {
      return `https://www.youtube.com/embed/${u.searchParams.get('v')}?playsinline=1&autoplay=1&mute=1`;
    }
    if (host === 'youtu.be' && u.pathname.length > 1) {
      return `https://www.youtube.com/embed${u.pathname}?playsinline=1&autoplay=1&mute=1`;
    }
    if (host === 'vimeo.com' && /^\/\d+$/.test(u.pathname)) {
      return `https://player.vimeo.com/video${u.pathname}?playsinline=1&autoplay=1&muted=1`;
    }
  } catch {
    /* not a parseable URL — use as-is */
  }
  return url;
}

function onMarkPoiClicked(): void {
  const nav = activeNav();
  if (!nav || !nav.poseTransform.isLocalized) {
    guidance.textContent = 'Localize first before marking a POI.';
    guidance.classList.remove('hidden');
    return;
  }
  picker.hide();
  const pose = nav.poseTransform.snapshot(nav.camera);
  markPoiForm.show(pose.mapPosition, currentRoute?.destination.floor ?? 0);
}

function togglePlacingPoi(): void {
  if (placingPoi) {
    stopPlacingPoi();
    return;
  }
  const nav = activeNav();
  if (!nav || !nav.poseTransform.isLocalized) {
    guidance.textContent = 'Localize first before placing a POI.';
    guidance.classList.remove('hidden');
    return;
  }
  picker.hide();
  placingPoi = true;
  placeBtn.classList.add('active');
  tapCatcher.classList.remove('hidden');
  guidance.textContent = 'Tap on the AR view where you want to place the POI (assumes flat floor level).';
  guidance.classList.remove('hidden');
}

function stopPlacingPoi(): void {
  placingPoi = false;
  pendingSuggestion = null;
  placeBtn.classList.remove('active');
  tapCatcher.classList.add('hidden');
  guidance.classList.add('hidden');
}

/**
 * v8: chat-driven bookmark ("remember this as the break room"). Anyone —
 * staff or visitor — can trigger this via Ask NavMe; unlike the staff-only
 * 🎯 button it doesn't require being localized-and-signed-in to WRITE (the
 * tap position is all poi_suggestions needs), only to be localized enough
 * to raycast a floor point at all.
 */
function startPlacingSuggestion(s: { name: string; category?: string; description?: string }): void {
  const nav = activeNav();
  if (!nav || !nav.poseTransform.isLocalized) {
    guidance.textContent = 'Localize first before placing a POI.';
    guidance.classList.remove('hidden');
    return;
  }
  picker.hide();
  pendingSuggestion = s;
  placingPoi = true;
  placeBtn.classList.add('active');
  tapCatcher.classList.remove('hidden');
  guidance.textContent = t('concierge.tapToPlace');
  guidance.classList.remove('hidden');
}

tapCatcher.addEventListener('click', (ev: MouseEvent) => {
  if (!placingPoi) return;
  const nav = activeNav();
  if (!nav) return;

  const ndcX = (ev.clientX / window.innerWidth) * 2 - 1;
  const ndcY = -(ev.clientY / window.innerHeight) * 2 + 1;
  const point = nav.poseTransform.raycastFloor(nav.camera, ndcX, ndcY);

  if (!point) {
    guidance.textContent = "Couldn't find the floor there — try tapping lower on the screen.";
    return; // stay in placing mode so they can retry
  }
  if (pendingSuggestion) {
    const suggestion = pendingSuggestion;
    stopPlacingPoi();
    void submitPoiSuggestion({
      mapCode: loadConfig().code,
      name: suggestion.name,
      category: suggestion.category,
      description: suggestion.description,
      floor: currentRoute?.destination.floor ?? 0,
      position: point,
    })
      .then(() => {
        guidance.textContent = t('concierge.suggestionSaved');
        guidance.classList.remove('hidden');
        setTimeout(() => guidance.classList.add('hidden'), 3000);
      })
      .catch((err: unknown) => {
        logEvent('error', `poi suggestion submit failed: ${err instanceof Error ? err.message : String(err)}`);
        guidance.textContent = "Couldn't save that spot — try again.";
        guidance.classList.remove('hidden');
      });
    return;
  }
  stopPlacingPoi();
  markPoiForm.show(point, currentRoute?.destination.floor ?? 0);
});

overlayRoot.appendChild(hud);

function iconButton(icon: string, title: string, onClick: () => void): HTMLButtonElement {
  const b = document.createElement('button');
  b.className = 'icon-btn';
  b.title = title;
  b.textContent = icon;
  b.addEventListener('click', onClick);
  return b;
}

function relocalize(): Promise<void> | void {
  return mode === 'webxr' ? arSession?.relocalize() : eightWallSession?.relocalize();
}

function clearActiveRoute(): void {
  activeNav()?.navOverlay.clearRoute();
  currentRoute = null;
  noRouteDest = null;
  hideFallbackPrompt();
  debugPanel.setRoute(null);
  guidance.classList.add('hidden');
}

// ---------- Screens ----------

let lastLandingOpts: { mode: AppMode | null; configured: boolean } | null = null;
let sessionPrepared = false;

/**
 * UX stress-test finding (2026-07-18): a failed prepareSession() (auth/
 * map-code validation) used to leave Start AR disabled FOREVER with only
 * an error string — no way to recover short of reloading the page. Now
 * offers a Retry button that re-runs the exact same attempt in place.
 */
function attemptPrepareSession(note: HTMLElement, startBtn: HTMLButtonElement): void {
  note.textContent = t('landing.connecting');
  document.getElementById('session-retry-btn')?.remove();
  void prepareSession()
    .then(() => {
      sessionPrepared = true;
      startBtn.disabled = false;
      note.textContent = t('landing.ready');
    })
    .catch((err: unknown) => {
      note.textContent = `Could not authorize: ${err instanceof Error ? err.message : String(err)}`;
      const retryBtn = document.createElement('button');
      retryBtn.id = 'session-retry-btn';
      retryBtn.className = 'btn ghost';
      retryBtn.textContent = '↻ Retry';
      retryBtn.addEventListener('click', () => attemptPrepareSession(note, startBtn));
      note.after(retryBtn);
    });
}

function showLanding(opts: { mode: AppMode | null; configured: boolean }): void {
  lastLandingOpts = opts;
  app.innerHTML = `
    <div class="screen landing">
      <img class="logo" src="/logo.png" alt="NavMe logo" onerror="this.outerHTML='<div class=\'logo logo-fallback\'>🧭</div>'" />
      <h1>NavMe</h1>
      <p class="tagline">by MetaDigi Labs</p>
      <div class="lang-row" id="lang-row"></div>
      <p class="explain">${t('landing.explain')}</p>
      <button id="start-ar" class="btn primary" disabled>${t('landing.start')}</button>
      <p id="landing-note" class="muted"></p>
      <button id="open-portal" class="btn ghost">🌍 All venues</button>
    </div>
  `;
  const startBtn = document.getElementById('start-ar') as HTMLButtonElement;
  const note = document.getElementById('landing-note')!;
  landingNote = note;

  // Language chips — filtered to what THIS venue enables (Dashboard →
  // Languages, per map code). Persists per device; all visitor strings are
  // looked up via t() at render/speak time so the switch is instant.
  const langRow = document.getElementById('lang-row')!;
  void getEnabledLangs(loadConfig().code).then((enabled) => {
    // Venue no longer offers the saved language → fall back.
    if (!enabled.includes(getLang())) setLang(enabled[0]);
    for (const { code, label } of LANGS.filter((l) => enabled.includes(l.code))) {
      const chip = document.createElement('button');
      chip.className = `lang-chip${getLang() === code ? ' on' : ''}`;
      chip.textContent = label;
      chip.addEventListener('click', () => {
        if (getLang() === code) return;
        setLang(code);
        logEvent('session', `language → ${code}`);
        if (lastLandingOpts) showLanding(lastLandingOpts); // re-render in new language
      });
      langRow.appendChild(chip);
    }
    // Single-language venues don't need a picker at all.
    if (enabled.length <= 1) langRow.classList.add('hidden');
  });

  document.getElementById('open-portal')!.addEventListener('click', () => {
    location.href = '/';
  });
  // Partner embeds get visitor-only chrome: no portal escape.
  if (isEmbedded()) {
    document.getElementById('open-portal')!.classList.add('hidden');
  }
  emitEmbed('ready');

  // 🏢 Corporate pack: visitor check-in from the landing screen. After
  // checking in, the visitor's first localization auto-routes to reception
  // (reuses the deep-link pendingDestId mechanism).
  if (isConfigComplete(loadConfig())) {
    void loadFeatures(loadConfig().code).then(() => {
      if (!featureEnabled('checkin') || document.getElementById('checkin-btn')) return;
      const b = document.createElement('button');
      b.id = 'checkin-btn';
      b.className = 'btn ghost';
      b.textContent = t('chk.title');
      b.addEventListener('click', () => {
        checkinSheet.show((draft) => {
          if (!draft) return;
          void submitCheckin(draft)
            .then(() => getPois())
            .then((pois) => {
              showToastMsg(t('chk.done'));
              // UX stress-test finding (2026-07-18): the button used to
              // stay live after a successful check-in — an impatient
              // second tap (or an accidental one) created a duplicate
              // check-in row with no indication anything was wrong.
              b.disabled = true;
              b.textContent = t('chk.checkedIn');
              const reception = pois.find(
                (p) =>
                  /reception|front desk|lobby/i.test(p.name) || /reception|front desk|lobby/i.test(p.category),
              );
              if (reception) {
                pendingDestId = reception.id; // first localization routes there
                logEvent('session', `check-in → will route to ${reception.name}`);
              }
            })
            .catch((err: unknown) => showToastMsg(err instanceof Error ? err.message : String(err)));
        });
      });
      note.after(b);
    });
  }

  if (!opts.mode) {
    startBtn.classList.add('hidden'); // no AR here — don't tease a dead button
    note.textContent =
      'AR navigation needs a phone (iPhone Safari or ARCore Android Chrome). On this device you can browse the 2D venue map below, or open this URL on your phone to navigate.';
    // AR-fallback: the venue is still explorable as a 2D floor plan.
    if (isConfigComplete(loadConfig())) {
      const mapBtn = document.createElement('button');
      mapBtn.className = 'btn ghost';
      mapBtn.textContent = t('map2d.viewBtn');
      mapBtn.addEventListener('click', () => openMap2D());
      note.after(mapBtn);
    }
    return;
  }
  if (!opts.configured) {
    note.textContent =
      'This venue is not configured on this deployment yet — pick a venue from the portal (🌍 All venues), or ask staff to complete Setup.';
    return;
  }

  // Language re-renders reuse the already-prepared session (no re-auth).
  if (sessionPrepared) {
    startBtn.disabled = false;
    note.textContent = t('landing.ready');
  } else {
    attemptPrepareSession(note, startBtn);
  }

  // IMPORTANT: startSession() must be the first call in the gesture handler
  // (both WebXR's requestSession and 8th Wall's XR8.run camera prompt require this).
  startBtn.addEventListener('click', () => {
    const session = mode === 'webxr' ? arSession : eightWallSession;
    armStartupWatchdog(); // surface a Retry if we never reach Localized (missed prompt / backgrounded)
    void session?.startSession().catch((err: unknown) => {
      statusView.set(
        LocalizationState.PermissionNeeded,
        err instanceof Error ? err.message : 'Camera permission was denied',
      );
      hud.classList.remove('hidden');
    });
  });
}

function showSetup(): void {
  sessionPrepared = false; // credentials may change — re-prepare on return
  app.innerHTML = '';
  const setup = new SetupScreen(() => void init());
  app.appendChild(setup.el);
}

function showPortal(): void {
  app.innerHTML = `<div class="screen"><p class="muted">Loading venues…</p></div>`;
  void import('./components/PortalScreen').then(({ PortalScreen }) => {
    app.innerHTML = '';
    app.appendChild(new PortalScreen({ onDocs: showDocs, onAdmin: showAdmin }).el);
  });
}

function showDocs(): void {
  app.innerHTML = `<div class="screen"><p class="muted">Loading docs…</p></div>`;
  void import('./components/DocsScreen').then(({ DocsScreen }) => {
    app.innerHTML = '';
    app.appendChild(new DocsScreen(() => void init()).el);
  });
}

function showAdmin(): void {
  app.innerHTML = `<div class="screen"><p class="muted">Loading dashboard…</p></div>`;
  void import('./components/AdminScreen').then(({ AdminScreen }) => {
    app.innerHTML = '';
    const admin = new AdminScreen(() => void init(), showSetup);
    app.appendChild(admin.el);
  });
}

// ---------- Session wiring ----------

/**
 * Adds the depth-only scan-mesh occluder under the active nav's mapRoot
 * (once loaded). loadOccluder caches internally, so calling this on every
 * localization is cheap; the group simply re-parents if the mode switched.
 */
function attachOccluder(nav: { poseTransform: PoseTransformService }): void {
  if (diagDisabled('occluder')) return; // ?no=occluder — skip the scan depth mesh (bare-nav A/B)
  void loadOccluder(loadConfig()).then((group) => {
    if (group && group.parent !== nav.poseTransform.mapRoot) {
      nav.poseTransform.mapRoot.add(group);
    }
  });
}

/** POI media display layer (images/videos/3D objects anchored in the space). */
const mediaLayers = new WeakMap<PoseTransformService, MediaLayer>();

/** Explore mode: AR directory labels for every POI (🏷️ toggle). */
const exploreLayers = new WeakMap<PoseTransformService, ExploreLayer>();
let exploreOn = false;
function exploreLayerFor(nav: { poseTransform: PoseTransformService }): ExploreLayer {
  let layer = exploreLayers.get(nav.poseTransform);
  if (!layer) {
    layer = new ExploreLayer(nav.poseTransform, (poi) => {
      activeTour = null;
      void onDestinationChosen(poi);
    });
    exploreLayers.set(nav.poseTransform, layer);
  }
  return layer;
}
function attachMediaLayer(nav: { poseTransform: PoseTransformService }): void {
  if (!featureEnabled('media')) return; // super-disabled for this venue
  let layer = mediaLayers.get(nav.poseTransform);
  if (!layer) {
    layer = new MediaLayer(nav.poseTransform, openLinkOverlay, (poiId) => track('media_impression', poiId));
    mediaLayers.set(nav.poseTransform, layer);
  }
  void layer.load();
}

async function prepareSession(): Promise<void> {
  // Token auth alone only proves clientId/secret are valid — the token
  // endpoint knows nothing about maps, so a wrong map code would otherwise
  // slip through until the first localization query. Validate it here so
  // Start AR stays disabled with a clear message instead.
  await validateMapCode();
  if (mode === 'webxr') {
    await prepareWebXRSession();
  } else if (mode === 'camera') {
    await prepareEightWallSession();
  }
}

async function validateMapCode(): Promise<void> {
  const config = loadConfig();
  if (config.mapType !== 'map') return; // TODO: no MapSet detail endpoint wired up yet
  let details;
  try {
    details = await new MultisetRestClient(config).getMapDetails(config.code);
  } catch (err) {
    const msg = err instanceof Error ? err.message : String(err);
    throw new Error(
      /404|not found/i.test(msg)
        ? `Map code "${config.code}" not found — check it in Setup`
        : msg,
    );
  }
  if (details.status !== 'active') {
    throw new Error(`Map "${config.code}" is not active yet (status: ${details.status})`);
  }
}

// ---------- First-run tutorial (feature-aware, skippable) ----------
const tutorial = new TutorialOverlay();
document.body.appendChild(tutorial.el);
// v4 (2026-07-18, explicit user choice — asked "once per browser session"
// vs "every AR start" vs "once-ever + manual replay" and this is what
// they picked): shows on EVERY AR session start (fresh launch, or
// stopping and starting AR again within the same visit) — no persistence
// at all. This deliberately reintroduces the repeat behavior that v2/v3
// (2026-07-12/17) removed because it got annoying on RELOCALIZE — that's
// still fine here since relocalize() doesn't create a new session / fire
// onSessionStart (see relocalize()), so this only repeats on genuine AR
// (re)starts, not mid-session background hiccups.
/** Deferred action (e.g. opening the POI picker after localization) that
 *  fires once the tutorial closes — nothing competes with it on screen. */
let afterTutorial: (() => void) | null = null;

/** Shown every AR session start — steps reflect the venue's features. */
function showTutorial(): void {
  const steps: TutorialStep[] = [
    { icon: '📡', text: t('tut.localize') },
    { icon: '🧭', text: t('tut.navigate') },
  ];
  // Secondary tools fade in on a screen tap (2026-07-18: replaced the old
  // ⋯ fold-out) — tell first-timers where they live.
  if (
    ['relocalize', 'voiceToggle', 'map2d', 'explore', 'parking', 'concierge', 'arAddPoi', 'arPinPoint', 'debugTools']
      .some((k) => featureEnabled(k))
  ) {
    steps.push({ icon: '👆', text: t('tut.more') });
  }
  if (featureEnabled('concierge')) steps.push({ icon: '💬', text: t('tut.concierge') });
  if (featureEnabled('voiceToggle')) steps.push({ icon: '🗣', text: t('tut.voice') });
  if (featureEnabled('map2d')) steps.push({ icon: '🗺', text: t('tut.map2d') });
  if (featureEnabled('explore')) steps.push({ icon: '🏷', text: t('tut.explore') });
  if (featureEnabled('parking')) steps.push({ icon: '🚗', text: t('tut.parking') });
  if (featureEnabled('capture')) steps.push({ icon: '📸', text: t('tut.capture') });
  picker.hide(); // tutorial gets the whole screen — no POI list behind it
  tutorial.show(steps, (skipped) => {
    logEvent('session', skipped ? 'tutorial skipped' : 'tutorial finished');
    const cb = afterTutorial;
    afterTutorial = null;
    cb?.();
  });
  logEvent('session', `tutorial shown (${steps.length} steps)`);
}

// ---------- Localization coach (AI agent 2) ----------
// Slow first fix or a Relocalizing stretch → one short, spoken instruction
// built from the venue's own localization history (see locCoachService).
let coachFirstFixTimer: number | null = null;
let coachHadFix = false;

function coachNow(kind: 'first-fix' | 'relocalizing'): void {
  if (!featureEnabled('locCoach')) return; // per-map AI-agent toggle
  const nav = activeNav();
  const snap = nav && nav.poseTransform.isLocalized ? nav.poseTransform.snapshot(nav.camera) : null;
  void coachLocalization(kind, snap?.mapPosition ?? null, snap?.headingRad ?? null).then((msg) => {
    if (msg) {
      showToastMsg(msg);
      speak(msg, 'coach', 15000);
    }
  });
}

/** Call from BOTH AR modes' onState. Self-throttled in the service. */
function coachOnState(state: LocalizationState): void {
  if (state === LocalizationState.Localizing && !coachHadFix && coachFirstFixTimer === null) {
    coachFirstFixTimer = window.setTimeout(() => {
      coachFirstFixTimer = null;
      if (!coachHadFix) coachNow('first-fix');
    }, 9000);
  } else if (state === LocalizationState.Localized) {
    coachHadFix = true;
    if (coachFirstFixTimer !== null) {
      window.clearTimeout(coachFirstFixTimer);
      coachFirstFixTimer = null;
    }
  } else if (state === LocalizationState.Relocalizing) {
    coachNow('relocalizing');
  }
}

async function prepareWebXRSession(): Promise<void> {
  arSession?.dispose();
  arSession = new NavMeARSession(
    loadConfig(),
    { renderer, scene, camera },
    {
      onState: (state, detail) => {
        statusView.set(state, detail);
        debugPanel.setState(state);
        logEvent('state', detail ? `${state} — ${detail}` : state);
        // Pause guidance only on genuine tracking problems — routine
        // background re-localization ('Localizing…') shouldn't blink the UI.
        if (
          state === LocalizationState.TrackingWeak ||
          state === LocalizationState.LostTracking ||
          state === LocalizationState.Error
        ) {
          webxrNav.navOverlay.setGuidanceVisible(false);
        } else if (state === LocalizationState.Localized) {
          webxrNav.navOverlay.setGuidanceVisible(true);
        }
        // Relocalizing (3+ consecutive VPS misses) keeps the path/arrow
        // visible so brief outages don't blink the UI, but the pose is
        // running on uncorrected drift the whole time — stale-flag it so
        // floor-change voice cues don't fire on a drifted height estimate.
        if (state === LocalizationState.Relocalizing) localizationStale = true;
        else if (state === LocalizationState.Localized) localizationStale = false;
        coachOnState(state);
        arFallbackOnState(state);
        watchdogOnState(state);
      },
      onLocalized: (result, worldFromMap) => {
        markLocalized(); // records time-to-first-fix (no-op after the first)
        lastVpsOkAt = performance.now();
        webxrNav.poseTransform.setWorldFromMap(worldFromMap);
        attachOccluder(webxrNav);
        attachMediaLayer(webxrNav);
        const p = result.localizeData?.position;
        if (p) {
          debugPanel.setLastLocalization(`pos ${p.x.toFixed(2)}, ${p.y.toFixed(2)}, ${p.z.toFixed(2)}`);
          recordLocSample({ x: p.x, y: p.y, z: p.z, c: result.localizeData?.confidence ?? 0.5 });
        }
        statsHud.setConfidence(result.localizeData?.confidence ?? null);
        onFirstLocalized();
      },
      onFrame: () => onXRFrame(),
      onSessionStart: () => {
        markArSessionStart();
        resetCoach();
        showTutorial();
        app.classList.add('hidden');
        hud.classList.remove('hidden');
      },
      onSessionEnd: () => {
        emitEmbed('closed');
        persistSession('webxr session end');
        mediaLayers.get(webxrNav.poseTransform)?.pauseAll();
        closeLinkOverlay();
        activeTour = null;
        notifiedPois.clear();
        hud.classList.add('hidden');
        app.classList.remove('hidden');
        arrivalBanner.classList.add('hidden');
        picker.hide();
        stopPlacingPoi();
        clearActiveRoute();
        webxrNav.poseTransform.reset();
        hasShownPicker = false;
      },
    },
  );
  await arSession.authorize();
  arSession.initialize(overlayRoot);
}

async function prepareEightWallSession(): Promise<void> {
  eightWallSession?.dispose();
  cameraModeNav = null;
  eightWallSession = new EightWallSession(
    loadConfig(),
    { canvas: eightWallCanvas },
    {
      onState: (state, detail) => {
        statusView.set(state, detail);
        debugPanel.setState(state);
        logEvent('state', detail ? `${state} — ${detail}` : state);
        // Before the AR session actually starts (hud still hidden), surface
        // progress on the landing screen too — this is what turns a hung
        // "Loading AR engine…" / "Authenticating…" step into something
        // visible instead of a frozen "Connecting to MultiSet…".
        if (hud.classList.contains('hidden') && detail) setLandingNote(detail);
        if (
          state === LocalizationState.TrackingWeak ||
          state === LocalizationState.LostTracking ||
          state === LocalizationState.Error
        ) {
          cameraModeNav?.navOverlay.setGuidanceVisible(false);
        } else if (state === LocalizationState.Localized) {
          cameraModeNav?.navOverlay.setGuidanceVisible(true);
        }
        // Relocalizing (3+ consecutive VPS misses) keeps the path/arrow
        // visible so brief outages don't blink the UI, but the pose is
        // running on uncorrected drift the whole time — stale-flag it so
        // floor-change voice cues don't fire on a drifted height estimate.
        if (state === LocalizationState.Relocalizing) localizationStale = true;
        else if (state === LocalizationState.Localized) localizationStale = false;
        coachOnState(state);
        arFallbackOnState(state);
        watchdogOnState(state);
      },
      onSceneReady: ({ scene: ewScene, camera: ewCamera }) => {
        const poseTransform = new PoseTransformService();
        const navOverlay = new NavigationOverlay(poseTransform, ewScene, ewCamera);
        cameraModeNav = { poseTransform, navOverlay, camera: ewCamera };
      },
      onLocalized: (position, worldFromMap, confidence) => {
        markLocalized(); // records time-to-first-fix (no-op after the first)
        lastVpsOkAt = performance.now();
        // Confidence gates HOW FAR a fix may move the world (low-conf fixes
        // can't apply medium/snap corrections — see poseTransformService).
        cameraModeNav?.poseTransform.setWorldFromMap(worldFromMap, confidence);
        if (cameraModeNav) {
          attachOccluder(cameraModeNav);
          attachMediaLayer(cameraModeNav);
        }
        debugPanel.setLastLocalization(
          `pos ${position.x.toFixed(2)}, ${position.y.toFixed(2)}, ${position.z.toFixed(2)}`,
        );
        statsHud.setConfidence(confidence ?? null);
        onFirstLocalized();
      },
      onFrame: () => onXRFrame(),
      onSessionStart: () => {
        markArSessionStart();
        resetCoach();
        showTutorial();
        // eightWallCanvas is intentionally never toggled with .hidden — see
        // the comment on it in index.html. #app covers it visually until now.
        app.classList.add('hidden');
        hud.classList.remove('hidden');
      },
      onSessionEnd: () => {
        emitEmbed('closed');
        persistSession('camera session end');
        if (cameraModeNav) mediaLayers.get(cameraModeNav.poseTransform)?.pauseAll();
        closeLinkOverlay();
        activeTour = null;
        notifiedPois.clear();
        hud.classList.add('hidden');
        app.classList.remove('hidden');
        arrivalBanner.classList.add('hidden');
        picker.hide();
        stopPlacingPoi();
        clearActiveRoute();
        cameraModeNav?.poseTransform.reset();
        hasShownPicker = false;
      },
    },
  );
  await eightWallSession.authorize();
  eightWallSession.initialize();
}

// Deep link support: ?dest=<poiId> auto-routes after first localization
// (QR posters at venue entrances). Cleared once consumed.
let pendingDestId: string | null = new URLSearchParams(location.search).get('dest');
let pendingTourId: string | null = new URLSearchParams(location.search).get('tour');
// 🏥 Healthcare pack: ?appt=<label> personalizes the arrival ("Appointment
// check-in complete") and tracks an appt_arrived analytics event — put it in
// appointment-confirmation QR codes/links together with ?dest=.
// ?aud=patient|visitor scopes the POI catalog to that audience (see
// audience filter; feature 'audienceModes').
const apptLabel: string | null = new URLSearchParams(location.search).get('appt');
const audienceParam: string | null = new URLSearchParams(location.search).get('aud');
if (audienceParam) logEvent('session', `audience mode: ${audienceParam}`);

/** First successful localization: deep-linked destination or the picker. */
function onFirstLocalized(): void {
  if (hasShownPicker) return;
  hasShownPicker = true;
  track('session_localized');
  emitEmbed('localized');
  if (pendingTourId) {
    const id = pendingTourId;
    pendingTourId = null;
    void getTours().then((tours) => {
      const tour = tours.find((tt) => tt.id === id);
      if (tour) startTour(tour);
      else showPicker();
    });
    return;
  }
  if (pendingDestId) {
    const id = pendingDestId;
    pendingDestId = null;
    void getPoiById(id).then((poi) => {
      if (poi) {
        logEvent('route', `deep link → ${poi.name}`);
        void onDestinationChosen(poi);
      } else {
        logEvent('error', `deep link dest "${id}" not found`);
        showPicker();
      }
    });
    return;
  }
  showPicker();
}

async function onDestinationChosen(poi: POI): Promise<void> {
  picker.hide();
  const nav = activeNav();
  if (!nav || !nav.poseTransform.isLocalized) {
    // Ask NavMe doesn't gate chat on localization (user 2026-07-18: "dont
    // worry about localization error" while just talking) — so a chat-driven
    // "take me there" can land here before the first VPS fix ever comes in.
    // Don't dead-end silently: queue it like a deep link (onFirstLocalized()
    // auto-starts it the moment we localize) and nudge the coach to help the
    // fix land sooner, since navigation was explicitly requested now.
    pendingDestId = poi.id;
    guidance.textContent = t('route.waitingForLoc', { name: poi.name });
    guidance.classList.remove('hidden');
    coachNow('first-fix');
    return;
  }

  const pose = nav.poseTransform.snapshot(nav.camera);
  const route = await calculateRoute(pose.mapPosition, poi, {
    accessibleOnly: picker.accessibleOnly,
  });
  if (!route) {
    guidance.textContent = t('route.none', { name: poi.name });
    guidance.classList.remove('hidden');
    // No walkable route (e.g. the scan's floors aren't connected) — don't
    // dead-end the user: the 2D map shows a dashed direction hint to the
    // destination (user 2026-07-12: "if you failed AR immediately show in
    // 2D"), with an honest message (routing failed — NOT a camera problem).
    if (featureEnabled('map2d')) {
      noRouteDest = poi;
      speak(t('map2d.noroute'), 'no-route', 10_000);
      showFallbackPrompt(t('map2d.noroute'));
    }
    return;
  }
  noRouteDest = null;
  hideFallbackPrompt();
  currentRoute = route;
  arrivalBanner.classList.add('hidden');
  // Pass the camera's map-space height so breadcrumbs project to floor level.
  nav.navOverlay.setRoute(route, pose.mapPosition.y);
  debugPanel.setRoute(route);
  guidance.classList.remove('hidden');
  speak(t('voice.start', { name: poi.name, m: Math.round(route.distance) }), 'route-start', 2000);
  track('route_start', poi.id);
  emitEmbed('route_start', { poiId: poi.id, distanceM: Math.round(route.distance) });
}

function stopEverything(): void {
  activeTour = null;
  stopPlacingPoi();
  clearActiveRoute();
  if (mode === 'webxr') arSession?.stopSession();
  else eightWallSession?.stopSession();
}

// ---------- Per-frame guidance ----------

/** Average indoor walking speed for ETA estimates. */
const WALKING_SPEED_M_S = 1.2;

const updateHud = throttle(
  (g: { distanceToTurnM: number | null; distanceRemainingM: number; turnsRemaining: number; floorChange: 'up' | 'down' | null; turnAround: boolean; trailGuide: TrailGuide }, destName: string) => {
    const etaMin = Math.max(1, Math.ceil(g.distanceRemainingM / WALKING_SPEED_M_S / 60));
    const turnsTxt =
      g.turnsRemaining === 0
        ? ''
        : ` · ${g.turnsRemaining === 1 ? t('hud.turn1') : t('hud.turns', { n: g.turnsRemaining })}`;
    const floorTxt =
      g.floorChange === 'up'
        ? `${t('hud.floorUp')} · `
        : g.floorChange === 'down'
          ? `${t('hud.floorDown')} · `
          : '';
    // #36 off-screen trail guidance: when the crumbs aren't in view, steer the
    // user's heading back to them ("turn left/right to the trail") instead of
    // showing a distance they can't see. Drift-tolerant coarse cue; turn-around
    // (debounced) still takes priority for the fully-behind case.
    const trailCue =
      g.trailGuide === 'left'
        ? '⬅ Turn left to the trail'
        : g.trailGuide === 'slight-left'
          ? '⬅ Trail slightly left'
          : g.trailGuide === 'right'
            ? 'Turn right to the trail ➡'
            : g.trailGuide === 'slight-right'
              ? 'Trail slightly right ➡'
              : null;
    const lead = g.turnAround
      ? `<strong>${t('hud.turnAround')}</strong>`
      : trailCue
        ? `<strong>${trailCue}</strong>`
        : g.distanceToTurnM !== null
          ? t('hud.toNextTurn', { d: `<strong>${formatMetres(g.distanceToTurnM)}</strong>` })
          : t('hud.straight');
    // escapeHtml: destName comes from the database — see utils/html.ts.
    guidance.innerHTML = `${floorTxt}${lead} · ${formatMetres(g.distanceRemainingM)}${turnsTxt} · ${t('hud.eta', { n: etaMin })} <strong>${escapeHtml(destName)}</strong>`;
  },
  250,
);

const updateDebugPose = throttle((nav: NavContext) => {
  if (nav.poseTransform.isLocalized) debugPanel.setPose(nav.poseTransform.snapshot(nav.camera));
}, 500);

// Pose trail for session replay (Dashboard 🚶 Trail) — 1 Hz, map space.
// Also feeds the walking-speed estimate (soft-arrival gate).
let speedPrev: { x: number; z: number; t: number } | null = null;
let lastSpeedMps = 0;
function userSpeedMps(): number {
  return lastSpeedMps;
}
const recordTrail = throttle((nav: NavContext) => {
  if (!nav.poseTransform.isLocalized) return;
  const p = nav.poseTransform.snapshot(nav.camera).mapPosition;
  const now = performance.now();
  if (speedPrev && now - speedPrev.t > 400) {
    lastSpeedMps = Math.hypot(p.x - speedPrev.x, p.z - speedPrev.z) / ((now - speedPrev.t) / 1000);
  }
  speedPrev = { x: p.x, z: p.z, t: now };
  recordPoseSample(p.x, p.y, p.z);
  if (wantsPositionStream()) emitEmbed('position', { x: p.x, y: p.y, z: p.z });
}, 1000);

const updateFps = throttle((fps: number) => statsHud.setFps(fps), 250);

// ---------- Adaptive performance guard (2026-07-12, user: "FPS was higher
// when no features were added") ----------
// The scan-mesh occluder is by far the biggest render cost we ever added
// (759k tris on this venue). If the frame rate stays clearly below the iOS
// camera-mode ceiling (~30 FPS) for 10 s straight, trade occlusion for
// smoothness automatically — crumbs may then show through walls, but a
// fluid camera matters more (navigation is the base feature). The 🐞 panel's
// 🧱 toggle can re-enable it manually; the guard never fights the user.
let lowFpsSince: number | null = null;
let occluderAutoDisabled = false;
const perfGuard = throttle((fps: number) => {
  if (occluderAutoDisabled || !hasOccluder() || !isOccluderEnabled()) return;
  if (fps > 0 && fps < 22) {
    if (lowFpsSince === null) lowFpsSince = performance.now();
    else if (performance.now() - lowFpsSince > 10_000) {
      occluderAutoDisabled = true;
      setOccluderEnabled(false);
      logEvent(
        'session',
        `perf guard: ${Math.round(fps)} FPS sustained 10s — occlusion auto-disabled (🐞 🧱 re-enables)`,
      );
    }
  } else {
    lowFpsSince = null;
  }
}, 1000);

/** Hide occluder chunks beyond ~25 m — distance culling on top of frustum
 *  culling (they'd contribute nothing visible at that range). */
const occluderLOD = throttle((pos: { x: number; z: number }) => updateOccluderLOD(pos), 500);

/** Last successful VPS fix — used to detect "VPS-blind" stretches (dark or
 *  unmapped areas where localization keeps failing). */
let lastVpsOkAt = 0;

function onXRFrame(): void {
  const fps = fpsMeter.tick();
  updateFps(fps);
  perfGuard(fps);
  recordFps(fps); // #33: surface FPS (min/avg/last) in the 🐞 export
  const nav = activeNav();
  if (!nav) return;
  nav.poseTransform.tick(nav.camera); // ease corrections, pivoting about the camera
  if (nav.poseTransform.isLocalized) {
    occluderLOD(nav.poseTransform.snapshot(nav.camera).mapPosition);
  }
  mediaLayers.get(nav.poseTransform)?.update(nav.camera);
  exploreLayers.get(nav.poseTransform)?.update(nav.camera);
  updateDebugPose(nav);
  recordTrail(nav);
  checkProximity(nav);
  if (!currentRoute) return;

  const g = nav.navOverlay.update(performance.now());
  if (!g) return;
  debugPanel.setNextIndex(g.nextNodeIndex);

  // SOFT ARRIVAL in VPS-blind zones (2026-07-12 session: the office was too
  // dark for VPS — "no pose" ×4 — so the stale anchor kept insisting the
  // destination was "2.9 m ahead" while the user stood IN it, and arrival
  // never fired). If we're within a few metres, VPS hasn't landed a fix for
  // 12 s, and the user has effectively stopped, the residual distance is
  // mostly anchor error: call it arrived rather than stranding them.
  // VERTICAL GATE (2026-07-12 multi-floor session: user stood UPSTAIRS at
  // y≈5.7 directly above the Kitchen at y≈0.6 — XZ remaining was 2.6 m and
  // soft arrival fired a floor and a half too early). distanceRemainingM is
  // horizontal; require the user's FLOOR (camera y − ~1.4 m phone height)
  // to match the destination's elevation before soft-arriving.
  const softPos = nav.poseTransform.snapshot(nav.camera).mapPosition;
  const sameFloorAsDest = Math.abs(softPos.y - 1.4 - currentRoute.destination.position.y) < 2.0;
  if (
    !g.arrived &&
    !g.poseSuspect && // G5: a diving height estimate is not a position to arrive from
    g.distanceRemainingM < 4.5 &&
    sameFloorAsDest &&
    lastVpsOkAt > 0 &&
    performance.now() - lastVpsOkAt > 12_000 &&
    userSpeedMps() < 0.25
  ) {
    logEvent('route', `soft arrival: ${g.distanceRemainingM.toFixed(1)}m remaining but VPS blind ${(Math.round((performance.now() - lastVpsOkAt) / 1000))}s and user stationary — anchor error, calling it arrived`);
    nav.navOverlay.forceArrived(); // stop rendering guidance too
    g.arrived = true;
  }

  // A fresh ≥1 m world correction moves the user marker, not the user —
  // don't let it trigger arrival (or, below, off-route recalc).
  const correctionFresh = nav.poseTransform.recentCorrection(0.8, 5000);
  if (g.arrived && !correctionFresh) {
    const destName = currentRoute.destination.name;
    const destDescription = currentRoute.destination.description; // exhibit narration
    track('route_arrived', currentRoute.destination.id);
    emitEmbed('arrived', { poiId: currentRoute.destination.id });
    speak(t('voice.arrived', { name: destName }), 'arrived', 5000);
    vibrate([100, 60, 100]);
    // 🏥 Appointment deep link: confirm the check-in on arrival.
    if (apptLabel && featureEnabled('appointments')) {
      showToastMsg(t('appt.arrived', { name: destName }));
      track('appt_arrived', currentRoute.destination.id);
      logEvent('session', `appointment arrival confirmed (${apptLabel})`);
    }
    // Arrival auto-capture: photo + configured-length video, uploaded for the
    // Dashboard's Captures card. TWO gates: the venue-level 'capture' feature
    // flag AND the POI's own opt-in (Dashboard Add/Edit POI → 📸 checkbox).
    // Capture-enabled arrivals REPLACE the arrival banner with the recording
    // overlay (big countdown, "move around"), then a review sheet (photo/
    // video preview + rating + notes → JSON next to the media, folded into
    // the PDF report); the banner shows once the review closes. Mid-tour
    // stops capture silently — the review sheet would block auto-advance.
    const capturing = featureEnabled('capture') && currentRoute.destination.capture === true;
    const midTour = activeTour !== null && activeTour.idx < activeTour.tour.poiIds.length - 1;
    if (featureEnabled('capture')) {
      if (capturing) {
        const dest = currentRoute.destination;
        void autoCaptureAtPoi(canvas, dest.name)
          .then((res) => {
            if (!res) {
              // Another capture was already running — fall back to the banner.
              if (!midTour) showArrivalBanner();
              return;
            }
            if (midTour) {
              // No review sheet mid-tour — queue the auto PDF report now.
              scheduleAutoReport();
              return;
            }
            captureReview.show(
              { poiName: dest.name, photoUrl: res.photo.url, videoUrl: res.video.url, seconds: res.seconds },
              (review) => {
                if (review && (review.notes || review.rating > 0)) {
                  void submitCaptureNote({
                    poiName: dest.name,
                    rating: review.rating || undefined,
                    notes: review.notes || undefined,
                    photoUrl: res.photo.url,
                    videoUrl: res.video.url,
                    at: Date.now(),
                  }).catch((err: unknown) =>
                    logEvent('session', `capture note failed: ${err instanceof Error ? err.message : String(err)}`),
                  );
                  showToastMsg(t('cap.saved'));
                }
                // Dashboard Captures UI is gone — the visitor flow emails the
                // PDF report itself (debounced; coalesces multi-stop visits).
                scheduleAutoReport();
                showArrivalBanner();
              },
            );
          })
          .catch((err: unknown) => {
            logEvent('session', `auto-capture failed: ${err instanceof Error ? err.message : String(err)}`);
            if (!midTour) showArrivalBanner();
          });
      } else {
        logEvent('session', `auto-capture skipped — not enabled for POI "${destName}"`);
      }
    }
    // Parking memory: arriving AT the car → offer to forget the spot so it
    // drops from the picker (user-requested; No keeps it).
    if (currentRoute.destination.id === 'personal-parking') {
      window.setTimeout(() => {
        if (confirm(t('park.arrivedPrompt'))) {
          clearParkingSpot();
          picker.setPersonalSpot(null);
          logEvent('session', 'parking spot deleted after arrival');
        }
      }, 600);
    }
    currentRoute = null;
    offRouteSince = null;

    // Tour mode: auto-advance to the next stop after a short pause.
    // 🖼 EXHIBIT MODE (museums pack): the visitor lingers at each exhibit —
    // no timed auto-advance. The destination's description is narrated and
    // a "Next exhibit ▶" button drives the pace instead.
    if (activeTour && activeTour.idx < activeTour.tour.poiIds.length - 1) {
      const tourRef = activeTour;
      if (featureEnabled('exhibitMode')) {
        guidance.innerHTML = `${escapeHtml(t('exh.stop', { n: tourRef.idx + 1, total: tourRef.tour.poiIds.length }))} — ${escapeHtml(destName)}`;
        guidance.classList.remove('hidden');
        if (destDescription) speak(destDescription, 'exhibit-story', 3000);
        exhibitNextBtn.textContent = t('exh.next');
        exhibitNextBtn.classList.remove('hidden');
        arrivalTitle.textContent = `${t('exh.stop', { n: tourRef.idx + 1, total: tourRef.tour.poiIds.length })} — ${destName}`;
        arrivalAgainBtn.textContent = t('arrival.again');
        arrivalBanner.classList.remove('hidden');
        logEvent('route', `exhibit ${tourRef.idx + 1}/${tourRef.tour.poiIds.length} reached — waiting for Next`);
        return;
      }
      guidance.innerHTML = t('tour.next', { name: escapeHtml(destName) });
      guidance.classList.remove('hidden');
      logEvent('route', `tour stop ${tourRef.idx + 1} reached — advancing`);
      setTimeout(() => {
        if (activeTour !== tourRef) return; // tour cancelled meanwhile
        activeTour = { tour: tourRef.tour, idx: tourRef.idx + 1 };
        void goToTourStop();
      }, 4000);
      return;
    }
    if (activeTour) {
      track('tour_complete');
      emitEmbed('tour_complete', { tourId: activeTour.tour.id });
      speak(t('tour.complete'), 'tour-done', 2000);
      logEvent('route', `tour "${activeTour.tour.name}" complete`);
      activeTour = null;
    }
    guidance.classList.add('hidden');
    // Capture-enabled arrivals show the banner AFTER the capture/review flow
    // (the recording overlay replaces the "you have arrived" message).
    if (!capturing) showArrivalBanner();
    return;
  }
  updateHud(g, currentRoute.destination.name);
  announceGuidance(g);
  checkOffRoute(nav, g.offPathM);
}

// ---------- Proximity notifications (opt-in) ----------
// "You're passing the Café" — once per POI per session, never for the
// current destination, only when the 🔔 toggle is on.
const PROXIMITY_DIST_M = 4;
const notifiedPois = new Set<string>();
const proxToast = document.createElement('div');
proxToast.className = 'prox-toast hidden';
hud.appendChild(proxToast);
let proxToastTimer: number | undefined;

const checkProximity = throttle((nav: NavContext) => {
  if (!featureEnabled('proximity')) return;
  if (!picker.proximityEnabled || !nav.poseTransform.isLocalized) return;
  const user = nav.poseTransform.snapshot(nav.camera).mapPosition;
  void getPois().then((pois) => {
    for (const poi of pois) {
      if (notifiedPois.has(poi.id)) continue;
      if (currentRoute?.destination.id === poi.id) continue;
      if (dist3(user, poi.position) >= PROXIMITY_DIST_M) continue;
      notifiedPois.add(poi.id);
      logEvent('session', `proximity: passing ${poi.name}`);
      speak(t('prox.passing', { name: poi.name }), 'proximity', 6000);
      proxToast.textContent = `📍 ${t('prox.passing', { name: poi.name })}`;
      proxToast.classList.remove('hidden');
      window.clearTimeout(proxToastTimer);
      proxToastTimer = window.setTimeout(() => proxToast.classList.add('hidden'), 4000);
    }
  });
}, 2000);

// ---------- Voice/haptic guidance announcements ----------
let turnAnnounced = false;
/** #36: last off-screen trail side spoken — so we announce once on transition, not every frame. */
let lastTrailSide: 'left' | 'right' | null = null;

function announceGuidance(g: {
  distanceToTurnM: number | null;
  floorChange: 'up' | 'down' | null;
  turnAround: boolean;
  trailGuide: TrailGuide;
}): void {
  if (g.turnAround) {
    speak(t('voice.turnAround'), 'turnaround', 8000); // deduped, 8s cooldown
  }
  // #36 off-screen trail: speak the side ONCE when the crumbs leave the view
  // (or when the side flips), not repeatedly. Turn-around owns the fully-behind
  // case. Drift-tolerant: a coarse "left/right" barely changes under ~1.6 m drift.
  const side =
    g.trailGuide === 'left' || g.trailGuide === 'slight-left'
      ? 'left'
      : g.trailGuide === 'right' || g.trailGuide === 'slight-right'
        ? 'right'
        : null;
  if (!g.turnAround && side && side !== lastTrailSide) {
    speak(side === 'left' ? 'The trail is on your left' : 'The trail is on your right', 'trailcue', 4000);
  }
  lastTrailSide = side;
  if (g.distanceToTurnM !== null && g.distanceToTurnM < 5 && !turnAnnounced) {
    turnAnnounced = true;
    speak(t('voice.turn', { m: Math.max(1, Math.round(g.distanceToTurnM)) }), 'turn', 4000);
    vibrate(80);
  }
  // Re-arm once the current turn is passed (or path straightens out).
  if (g.distanceToTurnM === null || g.distanceToTurnM > 7) turnAnnounced = false;

  // Skip while the pose is stale (VPS has been failing long enough to enter
  // 'Relocalizing…') — the camera position has been drifting uncorrected,
  // and a drifted height alone is enough to falsely trip the floor-change
  // check (see debug report 2026-07-11: ~3.5 min without a confirmed fix,
  // then a false "stairs down" the moment guidance re-armed on the still-
  // unconfirmed pose). distanceToTurnM/turn cues are left alone — a turn
  // fired a few seconds early/late from drift is a minor inconvenience,
  // versus "stairs" sending someone looking for steps that aren't there.
  if (g.floorChange && !localizationStale) {
    suppressedCueLogged = false;
    speak(
      g.floorChange === 'up' ? t('voice.stairsUp') : t('voice.stairsDown'),
      `floor-${g.floorChange}`,
      20000,
    );
  } else if (g.floorChange && localizationStale) {
    // Once per stale episode — this runs every frame and flooded the
    // 600-event ring buffer with ~150 identical lines (report 2026-07-12
    // 20:53, drowning out the events that mattered).
    if (!suppressedCueLogged) {
      suppressedCueLogged = true;
      logEvent('route', `floor-change ${g.floorChange} cue suppressed — pose stale (Relocalizing)`);
    }
  }
}
/** One "cue suppressed" log per Relocalizing episode (reset on fresh cue). */
let suppressedCueLogged = false;

// ---------- Off-route recalculation ----------
// Wander >2.5 m off the path for 3+ s and the route silently recomputes from
// the current position, instead of the arrow pointing backward forever.
//
// COOLDOWN (2026-07-11, debug report): a noisy/low-confidence VPS stretch
// (conf down to 0.35, 1-2.8m 'eased' pose jumps every ~4s re-sync) doesn't
// mean the user is actually wandering off the path repeatedly — it means
// the position ESTIMATE is jittering. Recalculating from a jittery position
// just produces another route that the next jitter immediately invalidates
// too, so "off-route (5.8m) — recalculating" + a "Route updated" voice cue
// fired 5 times in ~13 seconds in that report. RECALC_COOLDOWN_MS enforces a
// floor between successive recalculations regardless of how long offPathM
// has been over threshold — the arrow being briefly wrong for a few extra
// seconds is far less disruptive than the route (and voice) changing every
// 3 seconds. Also skip entirely while the pose is stale (Relocalizing) —
// recalculating from an unconfirmed position is worse than not recalculating.
const OFF_ROUTE_DIST_M = 2.5;
const OFF_ROUTE_GRACE_MS = 3000;
const RECALC_COOLDOWN_MS = 8000;
let offRouteSince: number | null = null;
let recalcInFlight = false;
let lastRecalcAt = -Infinity;
/** Once-per-episode latch for "recalc skipped" logs — the per-frame version
 *  wrote 147 identical lines in 4 s (2026-07-13 walk 1, issue #24), drowning
 *  the 600-event ring buffer. Reset whenever the user is back on route or a
 *  recalc actually fires. */
let recalcSkipLogged = false;

function checkOffRoute(nav: NavContext, offPathM: number): void {
  if (recalcInFlight || !currentRoute) return;
  if (offPathM <= OFF_ROUTE_DIST_M || nav.poseTransform.recentCorrection(0.8, 5000)) {
    offRouteSince = null;
    recalcSkipLogged = false;
    return;
  }
  const now = performance.now();
  if (offRouteSince === null) {
    offRouteSince = now;
    return;
  }
  if (now - offRouteSince < OFF_ROUTE_GRACE_MS) return;

  if (now - lastRecalcAt < RECALC_COOLDOWN_MS) {
    // Stay armed (don't reset offRouteSince) so we fire as soon as the
    // cooldown clears if still off-route, instead of waiting a fresh 3s.
    return;
  }
  if (localizationStale) {
    if (!recalcSkipLogged) {
      recalcSkipLogged = true;
      logEvent('route', `off-route (${offPathM.toFixed(1)}m) — recalc skipped, pose stale (Relocalizing)`);
    }
    return;
  }
  if (nav.poseTransform.recentSnap(12_000)) {
    // Right after a big relocation the position is uncertain (during an
    // aliasing flip-flop it's actively contested) — recalculating from it
    // would trash a good route (2026-07-12 13:56 session).
    if (!recalcSkipLogged) {
      recalcSkipLogged = true;
      logEvent('route', `off-route (${offPathM.toFixed(1)}m) — recalc skipped, fresh snap correction (position settling)`);
    }
    return;
  }
  if (nav.poseTransform.holdingCorrection) {
    // #30 (Test 6): a large VPS correction is being HELD (unconfirmed) — the
    // displayed anchor disagrees with the localizer by metres, so offPathM is
    // measured from a contested position. 2026-07-13 Test 6: a Δ4.54 m hold at
    // t=53.2 s let an off-route recalc fire 1.3 s later ("Route updated"),
    // recomputing from a pose we already suspected was wrong. Same class as the
    // recent-snap guard above (cf. #28 for turn-around).
    if (!recalcSkipLogged) {
      recalcSkipLogged = true;
      logEvent('route', `off-route (${offPathM.toFixed(1)}m) — recalc skipped, correction held (anchor contested)`);
    }
    return;
  }

  offRouteSince = null;
  recalcSkipLogged = false;
  lastRecalcAt = now;
  recalcInFlight = true;
  const dest = currentRoute.destination;
  logEvent('route', `off-route (${offPathM.toFixed(1)}m) — recalculating to ${dest.name}`);
  void (async () => {
    try {
      const pose = nav.poseTransform.snapshot(nav.camera);
      const route = await calculateRoute(pose.mapPosition, dest, {
        accessibleOnly: picker.accessibleOnly,
      });
      if (route && currentRoute && currentRoute.destination.id === dest.id) {
        currentRoute = route;
        nav.navOverlay.setRoute(route, pose.mapPosition.y);
        debugPanel.setRoute(route);
        speak(t('voice.updated'), 'reroute', 10000);
      }
    } finally {
      recalcInFlight = false;
    }
  })();
}

// ---------- Boot ----------

async function init(): Promise<void> {
  // Returning from Google staff sign-in: tokens arrive in the URL hash and
  // ?admin=1 routes straight back to the Dashboard. Must run before any UI
  // reads isAuthed().
  consumeOAuthRedirect();
  const params = new URLSearchParams(location.search);
  if (params.get('admin') === '1') {
    history.replaceState(null, '', location.pathname);
    showAdmin();
    return;
  }

  // URL split: "/" is the public venue portal (world map + search); the AR
  // experience lives at /ar. ?venue=MAP_X on /ar selects the venue (the
  // credentials stay whatever this deployment/device already has — .env
  // defaults or Setup). Partner embeds always mean AR, whatever the path.
  const isArPath = location.pathname.replace(/\/+$/, '') === '/ar';
  if (!isArPath && !isEmbedded()) {
    showPortal();
    return;
  }
  const venueParam = (params.get('venue') ?? '').trim().toUpperCase();
  if (/^(MAP|MSET)_[A-Z0-9]+$/.test(venueParam)) {
    const cur = loadConfig();
    if (cur.code !== venueParam) {
      sessionPrepared = false;
      saveConfig({
        ...cur,
        code: venueParam,
        mapType: venueParam.startsWith('MSET_') ? 'map-set' : 'map',
      });
      logEvent('session', `venue from URL → ${venueParam}`);
    }
  }

  app.innerHTML = `<div class="screen"><p class="muted">Checking device…</p></div>`;

  // Navmesh (cached → authored /navmesh.glb → auto-generated from the
  // MultiSet scan mesh). Fire-and-forget: routing falls back to graph A* /
  // direct line until (and unless) it becomes available.
  const navCfg = loadConfig();
  if (isConfigComplete(navCfg)) void loadNavmesh(navCfg);
  // Feature flags (Super Admin–controlled, per venue). Fire-and-forget:
  // featureEnabled() defaults to true until loaded, then the gates apply.
  void loadFeatures(navCfg.code).then(() => {
    // MINIMAL HUD: only 🧭 (change destination) and ✕ (stop) are always
    // shown — every other button is feature-gated, defaultOff per catalog.
    exploreBtn.classList.toggle('hidden', !featureEnabled('explore'));
    parkBtn.classList.toggle('hidden', !featureEnabled('parking'));
    conciergeBtn.classList.toggle('hidden', !featureEnabled('concierge'));
    markBtn.classList.toggle('hidden', !featureEnabled('arAddPoi'));
    placeBtn.classList.toggle('hidden', !featureEnabled('arPinPoint'));
    relocBtn.classList.toggle('hidden', !featureEnabled('relocalize'));
    voiceBtn.classList.toggle('hidden', !featureEnabled('voiceToggle'));
    map2dBtn.classList.toggle('hidden', !featureEnabled('map2d'));
    facBtn.classList.toggle('hidden', !featureEnabled('facilityReq'));
    // 🔤 camera-mode only: the WebXR canvas has NO camera pixels (system-
    // composited — same limitation as auto-capture), a frame would be
    // AR-content-only and always read as 'unreadable'.
    translateBtn.classList.toggle('hidden', !featureEnabled('ocrTranslate') || mode === 'webxr');
    debugPanel.toggleButton.classList.toggle('hidden', !featureEnabled('debugTools'));
    picker.setFeatureGates({
      tours: featureEnabled('tours'),
      proximity: featureEnabled('proximity'),
      offers: featureEnabled('offers'),
      productFinder: featureEnabled('productFinder'),
      audienceModes: featureEnabled('audienceModes'),
      accessibleDefault: featureEnabled('accessibleDefault'),
    });
    // ?aud=patient|visitor|staff scopes the catalog (healthcare pack).
    if (audienceParam && ['all', 'patient', 'visitor', 'staff'].includes(audienceParam)) {
      picker.setAudience(audienceParam as 'all' | 'patient' | 'visitor' | 'staff');
    }
  });

  // Laptops/desktops: no touch + fine pointer = not an AR device, even if a
  // desktop browser technically reports WebXR (emulators/extensions). They
  // still get the Dashboard, Docs and Setup — just not a Start AR button.
  const isLikelyDesktop =
    navigator.maxTouchPoints === 0 && window.matchMedia('(pointer: fine)').matches;
  if (isLikelyDesktop) {
    mode = null;
    logEvent('session', 'desktop device detected — AR disabled');
  } else {
    const webxrSupported = await NavMeARSession.isSupported().catch(() => false);
    if (webxrSupported) {
      mode = 'webxr';
    } else {
      const eightWallSupported = await EightWallSession.isSupported().catch(() => false);
      mode = eightWallSupported ? 'camera' : null;
    }
  }

  // No local credentials? Run brokered: the multiset-token Edge Function
  // resolves the venue's credentials server-side (customer BYO account or
  // the platform account). Devices with local credentials (super testing)
  // keep direct auth.
  {
    const cfg = loadConfig();
    if (cfg.code && (!cfg.clientId || !cfg.clientSecret)) {
      saveConfig({ ...cfg, clientId: cfg.code, clientSecret: 'brokered', brokered: true });
      logEvent('session', `brokered auth for ${cfg.code}`);
    }
  }
  const configured = isConfigComplete(loadConfig());
  if (!configured) {
    // Nothing to run AR against — send them to the venue portal.
    showPortal();
    return;
  }
  showLanding({ mode, configured });
}

window.addEventListener('resize', () => {
  camera.aspect = window.innerWidth / window.innerHeight;
  camera.updateProjectionMatrix();
  renderer.setSize(window.innerWidth, window.innerHeight);
});

void init();
